import React, { FC, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { Form, useFormikContext } from 'formik';

// Components
import BasicChipsList from '../../../../components/display/chips/basicChipsList';
import { SearchableSelectType } from '../../../../components/inputs/searchableSelect';
import AampeCreateSelectDropdown from '../../../../components/shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
import styled from 'styled-components';

// Constants
import aampeTheme from '../../../../constants/aampeTheme';
import ICONS from '../../../../constants/icons';
import routes from '../../../../constants/routes';

// Hooks
import useGetGroupTestMessageUsers from '../../../../hooks/contact_notification_token/useGetGroupTestMessageUsers';

// Models
import { FormulaTestMessageFormValues } from 'models/formula.model';

interface TestFormulaGroupFormProps {
    providerId?: string;
}

const TestFormulaGroupForm: FC<TestFormulaGroupFormProps> = ({
    providerId,
}) => {
    const { values, handleBlur, setFieldValue } =
        useFormikContext<FormulaTestMessageFormValues>();
    const { data: groupTokens } = useGetGroupTestMessageUsers({
        push_provider_id: providerId,
    });

    const history = useHistory();
    const notificationTokenGroupOptions = useMemo<SearchableSelectType[]>(
        () =>
            groupTokens
                ? groupTokens.map((token) => ({
                      label: token.name,
                      value: token.id as string,
                  }))
                : [],
        [groupTokens],
    );

    return (
        <StyledForm>
            <SavedTokenContainer>
                <SavedTokenText>
                    If you have previously saved your token/receiver with a
                    group name you can select it from the dropdown
                </SavedTokenText>
                <AampeCreateSelectDropdown
                    label={'Group name'}
                    options={notificationTokenGroupOptions}
                    value={values.notification_token}
                    name={'notification_token'}
                    onChange={(
                        _,
                        value: SearchableSelectType | SearchableSelectType[],
                    ) => {
                        setFieldValue('notification_token', value);
                        const selectedGroupToken = groupTokens?.find(
                            (groupToken) =>
                                groupToken.id ===
                                (value as SearchableSelectType)?.value,
                        );
                        setFieldValue('users', selectedGroupToken?.users);
                        setFieldValue(
                            'contact_ids',
                            selectedGroupToken?.users?.map((u) => u.token),
                        );
                    }}
                    placeholder={'Select group'}
                    onBlur={handleBlur}
                />
            </SavedTokenContainer>
            {values.users?.length ? (
                <BasicChipsList
                    items={values.users}
                    onClickHandler={(id) => {
                        const updatedUsers = values.users.filter(
                            (user: any) => user.id !== id,
                        );
                        setFieldValue('users', updatedUsers);
                    }}
                    style={{ marginTop: `${aampeTheme.spacing(0)}` }}
                />
            ) : (
                <></>
            )}

            <HelpLink onClick={() => history.push(routes.testMessageSettings)}>
                <span>Create a new group </span>
                <ICONS.externalLink size={18} />
            </HelpLink>
        </StyledForm>
    );
};

const StyledForm = styled(Form)({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
});

const HelpLink = styled.span({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    color: aampeTheme.colors.solidA01,
    cursor: 'pointer',
    lineHeight: aampeTheme.size.lineHeight.small,
    fontWeight: aampeTheme.weights.medium,
    fontSize: aampeTheme.size.font.default,
});

const SavedTokenContainer = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
});

const SavedTokenText = styled.p({
    lineHeight: aampeTheme.size.lineHeight.default,
    fontWeight: aampeTheme.weights.light,
    fontSize: aampeTheme.size.font.default,
    color: aampeTheme.colors.neutral09,
});

export default TestFormulaGroupForm;
